import toast, { type Toast, type Renderable, type ValueOrFunction } from 'react-hot-toast';

/**
 * Generic toast utility with consistent styling
 */

interface ToastOptions {
  duration?: number;
  position?: 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right';
  minWidth?: string;
}

const defaultOptions: ToastOptions = {
  duration: 5000,
  position: 'bottom-right'
};

export const showToast = {
  /**
   * Show success toast
   */
  success: (message: string, options?: ToastOptions) => {
    return toast.success(message, {
      duration: options?.duration ?? defaultOptions.duration,
      position: options?.position ?? defaultOptions.position,
      style: {
        background: '#10b981',
        color: '#ffffff',
        padding: '12px 16px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
        ...(options?.minWidth && { minWidth: options.minWidth })
      },
      iconTheme: {
        primary: '#ffffff',
        secondary: '#10b981'
      }
    });
  },

  /**
   * Show custom success toast with JSX content
   */
  customSuccess: (content: ValueOrFunction<Renderable, Toast>, options?: ToastOptions) => {
    return toast.success(content, {
      duration: options?.duration ?? defaultOptions.duration,
      position: options?.position ?? defaultOptions.position,
      style: {
        background: '#10b981',
        color: '#ffffff',
        padding: '12px 16px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
        ...(options?.minWidth && { minWidth: options.minWidth })
      },
      iconTheme: {
        primary: '#ffffff',
        secondary: '#10b981'
      }
    });
  },

  /**
   * Show error toast
   */
  error: (message: string, options?: ToastOptions) => {
    return toast.error(message, {
      duration: options?.duration ?? defaultOptions.duration,
      position: options?.position ?? defaultOptions.position,
      style: {
        background: '#ef4444',
        color: '#ffffff',
        padding: '12px 16px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
        ...(options?.minWidth && { minWidth: options.minWidth })
      },
      iconTheme: {
        primary: '#ffffff',
        secondary: '#ef4444'
      }
    });
  },

  /**
   * Show custom error toast with JSX content
   */
  customError: (content: ValueOrFunction<Renderable, Toast>, options?: ToastOptions) => {
    return toast.error(content, {
      duration: options?.duration ?? defaultOptions.duration,
      position: options?.position ?? defaultOptions.position,
      style: {
        background: '#ef4444',
        color: '#ffffff',
        padding: '12px 16px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
        ...(options?.minWidth && { minWidth: options.minWidth })
      },
      iconTheme: {
        primary: '#ffffff',
        secondary: '#ef4444'
      }
    });
  },

  /**
   * Show info toast
   */
  info: (message: string, options?: ToastOptions) => {
    return toast(message, {
      duration: options?.duration ?? defaultOptions.duration,
      position: options?.position ?? defaultOptions.position,
      icon: 'ℹ️',
      style: {
        background: '#3b82f6',
        color: '#ffffff',
        padding: '12px 16px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
        ...(options?.minWidth && { minWidth: options.minWidth })
      }
    });
  },

  /**
   * Show custom info toast with JSX content
   */
  customInfo: (content: ValueOrFunction<Renderable, Toast>, options?: ToastOptions) => {
    return toast(content, {
      duration: options?.duration ?? defaultOptions.duration,
      position: options?.position ?? defaultOptions.position,
      icon: 'ℹ️',
      style: {
        background: '#3b82f6',
        color: '#ffffff',
        padding: '12px 16px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
        ...(options?.minWidth && { minWidth: options.minWidth })
      }
    });
  },

  /**
   * Show warning toast
   */
  warning: (message: string, options?: ToastOptions) => {
    return toast(message, {
      duration: options?.duration ?? defaultOptions.duration,
      position: options?.position ?? defaultOptions.position,
      icon: '⚠️',
      style: {
        background: '#f59e0b',
        color: '#ffffff',
        padding: '12px 16px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
        ...(options?.minWidth && { minWidth: options.minWidth })
      }
    });
  },

  /**
   * Show custom warning toast with JSX content
   */
  customWarning: (content: ValueOrFunction<Renderable, Toast>, options?: ToastOptions) => {
    return toast(content, {
      duration: options?.duration ?? defaultOptions.duration,
      position: options?.position ?? defaultOptions.position,
      icon: '⚠️',
      style: {
        background: '#f59e0b',
        color: '#ffffff',
        padding: '12px 16px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(245, 158, 11, 0.3)',
        ...(options?.minWidth && { minWidth: options.minWidth })
      }
    });
  },

  /**
   * Show loading toast
   */
  loading: (message: string, options?: ToastOptions) => {
    return toast.loading(message, {
      duration: options?.duration ?? defaultOptions.duration,
      position: options?.position ?? defaultOptions.position,
      style: {
        background: '#6b7280',
        color: '#ffffff',
        padding: '12px 16px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '500',
        boxShadow: '0 4px 12px rgba(107, 114, 128, 0.3)'
      }
    });
  },

  /**
   * Dismiss a specific toast
   */
  dismiss: (toastId?: string) => {
    toast.dismiss(toastId);
  },

  /**
   * Dismiss all toasts
   */
  dismissAll: () => {
    toast.dismiss();
  },

  /**
   * Promise toast - automatically updates based on promise state
   */
  promise: <T>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: Error) => string);
    },
    options?: ToastOptions
  ) => {
    return toast.promise(promise, messages, {
      position: options?.position ?? defaultOptions.position,
      style: {
        padding: '12px 16px',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '500'
      },
      success: {
        style: {
          background: '#10b981',
          color: '#ffffff',
          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
        },
        iconTheme: {
          primary: '#ffffff',
          secondary: '#10b981'
        }
      },
      error: {
        style: {
          background: '#ef4444',
          color: '#ffffff',
          boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
        },
        iconTheme: {
          primary: '#ffffff',
          secondary: '#ef4444'
        }
      },
      loading: {
        style: {
          background: '#6b7280',
          color: '#ffffff',
          boxShadow: '0 4px 12px rgba(107, 114, 128, 0.3)'
        }
      }
    });
  }
};

export default showToast;
